"use client";

import {
	RiCalendarLine,
	RiGovernmentLine,
	RiMapPinLine,
	RiTimeLine,
} from "@remixicon/react";
import {
	formatShortDateWithPreferences,
	formatTimeRangeWithPreferences,
	getShowDisplayParts,
	type ProcessedShow,
} from "@workspace/shared-utils";
import { Card, CardContent } from "@workspace/ui/components/card";
import { cn } from "@workspace/ui/lib/utils";
import Link from "next/link";
import { memo } from "react";
import { ShowPhoto } from "@/components/features/show/shared/show-photo";
import { Facepile } from "@/components/shared/facepile";
import { FacepileContainer } from "@/components/shared/facepile-skeleton";
import { useUserPreferences } from "@/hooks/shared/use-user-preferences";

interface ArtistShow {
	id: string;
	date: string;
	name: string;
	slug?: string;
	timeStart?: string | null;
	timeEnd?: string | null;
	imageUrl?: string | null;
	venue: {
		id: string;
		name: string;
		city?: string;
		state?: string;
		country?: string;
	};
	artists: Array<{
		id: string;
		name: string;
		slug: string;
		image_url?: string | null;
		role?: string; // headliner, opener, etc.
	}>;
	presentsName?: string | null;
	isCollaborative?: boolean;
	collaborationFormat?: "×" | "x" | "B2B" | "+" | "&" | "vs." | ",";
	collaborationAlias?: string | null;
	showArtists?: Array<{
		id: string;
		position: number;
		timeStart?: string;
		timeEnd?: string;
		isHeadliner: boolean;
		role: "headliner" | "collaborator" | "supporting_artist";
		artist: {
			id: string;
			name: string;
			imageUrl?: string | null;
		};
	}>;
}

interface ArtistShowCardProps {
	show: ArtistShow;
	attendees?: Array<{
		id: string;
		username: string | null;
		display_name: string;
		manual_avatar_url?: string | null;
		google_avatar_url?: string | null;
		rating?: number;
		loved?: boolean;
		must_see?: boolean;
		want_to_see?: boolean;
		saw_live?: boolean;
		saw_stream?: boolean;
		content?: string;
	}>;
	isFacepileLoading?: boolean;
}

const ArtistShowCard = memo(function ArtistShowCard({
	show,
	attendees = [],
	isFacepileLoading = false,
}: ArtistShowCardProps) {
	// Get user preferences for date formatting
	const userPreferences = useUserPreferences();

	// Get display parts for collaboration and presents handling
	const displayParts = getShowDisplayParts({
		id: show.id,
		title: show.name,
		artist_name: show.artists.find(a => a.role === 'headliner')?.name || show.name,
		date: show.date,
		location: [show.venue.city, show.venue.state || show.venue.country].filter(Boolean).join(', '),
		slug: show.slug || '',
		presents_name: show.presentsName,
		is_collaborative: show.isCollaborative,
		collaboration_format: show.collaborationFormat,
		collaboration_alias: show.collaborationAlias,
		show_artists: show.showArtists?.map(sa => ({
			id: sa.id,
			position: sa.position,
			time_start: sa.timeStart,
			time_end: sa.timeEnd,
			is_headliner: sa.isHeadliner,
			role: sa.role,
			artist: {
				id: sa.artist.id,
				name: sa.artist.name,
				image_url: sa.artist.imageUrl,
			}
		})),
	} as ProcessedShow);

	// Get image URL with fallback logic
	// Priority: poster > headliner artist image > null
	const imageUrl =
		show.imageUrl ||
		show.showArtists?.find((sa) => sa.isHeadliner)?.artist.imageUrl ||
		show.artists.find((a) => a.role === "headliner")?.image_url;

	return (
		<Card
			className={cn(
				"overflow-hidden",
				"hover:bg-muted/30 dark:hover:bg-muted/10",
				"active:bg-muted/50 dark:active:bg-muted/20",
				"active:scale-[0.99]",
				"transition-all duration-150",
			)}
		>
			<Link href={`/shows/${show.slug || show.id}`} className="block">
				<CardContent className="p-3">
					<div className="flex gap-3">
						<div className="h-16 w-16 flex-shrink-0">
							<ShowPhoto
								src={imageUrl}
								alt={show.name}
								priority={false}
								rounded="lg"
							/>
						</div>

						<div className="min-w-0 flex-1 flex flex-col justify-between">
							<div>
								<h3 className="line-clamp-1 font-semibold leading-tight">
									{displayParts.parts && displayParts.parts.length > 0 ? (
										displayParts.parts.map((part, index) => {
											const key = `${show.id}-part-${index}`;
											if (part.type === "artist") {
												return <span key={key}>{part.text}</span>;
											} else {
												return (
													<span
														key={key}
														className="ml-1 font-normal text-muted-foreground/50"
													>
														{part.text}
													</span>
												);
											}
										})
									) : (
										<>
											{displayParts.mainText}
											{displayParts.connector && (
												<span className="ml-1 font-normal text-muted-foreground/50">
													{displayParts.connector}
												</span>
											)}
											{displayParts.secondaryText && (
												<>
													{displayParts.connector && " "}
													{displayParts.secondaryText}
												</>
											)}
										</>
									)}
								</h3>
								<div className="space-y-0.5 text-muted-foreground text-xs">
									{show.venue && (
										<div className="flex items-center gap-1">
											<RiGovernmentLine className="h-3 w-3 flex-shrink-0" />
											<span className="line-clamp-1 text-left">{show.venue.name}</span>
										</div>
									)}
									{show.venue &&
										(show.venue.city ||
											show.venue.state ||
											show.venue.country) && (
											<div className="flex items-center gap-1">
												<RiMapPinLine className="h-3 w-3 flex-shrink-0" />
												<span className="line-clamp-1 text-left">
													{[
														show.venue.city,
														show.venue.state || show.venue.country,
													]
														.filter(Boolean)
														.join(", ")}
												</span>
											</div>
										)}
									<div className="flex items-center gap-1">
										<RiCalendarLine className="h-3 w-3 flex-shrink-0" />
										<span className="text-left">
											{formatShortDateWithPreferences(
												show.date,
												userPreferences,
											)}
										</span>
									</div>
									{/* Time row with facepile */}
									<div className="flex items-center justify-between gap-2">
										<div className="flex items-center gap-1">
											<RiTimeLine className="h-3 w-3 flex-shrink-0" />
											<span className="text-left">
												{show.timeStart
													? formatTimeRangeWithPreferences(
															show.timeStart,
															show.timeEnd || undefined,
															userPreferences,
														)
													: "Set Time: TBA"}
											</span>
										</div>

										{/* Facepile in same row as time */}
										<FacepileContainer hasData={attendees.length > 0}>
											<Facepile
												interactions={attendees}
												maxAvatars={4}
												size="sm"
												disableLinks={true}
											/>
										</FacepileContainer>
									</div>
								</div>
							</div>
						</div>
					</div>
				</CardContent>
			</Link>
		</Card>
	);
});

ArtistShowCard.displayName = "ArtistShowCard";

export { ArtistShowCard };
